import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock } from 'lucide-react';

const post = {
  title: 'Rekordbox XML Is Killing Your Library',
  subtitle: 'The export that erases your work, and why the fix requires going under the hood.',
  date: 'May 2026',
  readTime: '8 min read',
  tags: ['DJ', 'Metadata', 'Bonk'],
  slug: 'rekordbox-xml-is-killing-your-library',
};

export default function RekordboxXmlKillingYourLibrary() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', paddingTop: 100, paddingBottom: 120 }}>
      <div className="container" style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link
            to="/blog"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: 'var(--gray-3)',
              marginBottom: 48,
              transition: 'color 0.2s',
            }}
            onMouseEnter={e => (e.target.style.color = 'var(--accent)')}
            onMouseLeave={e => (e.target.style.color = 'var(--gray-3)')}
          >
            <ArrowLeft size={13} />
            Back to blog
          </Link>

          {/* Header */}
          <div style={{ marginBottom: 56 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--gray-3)', letterSpacing: '0.05em' }}>
                <Clock size={10} />
                {post.readTime}
              </span>
              <span style={{ color: 'var(--gray-4)', fontSize: 11 }}>·</span>
              <span style={{ fontSize: 11, color: 'var(--gray-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {post.date}
              </span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {post.tags.map(tag => (
                  <span
                    key={tag}
                    style={{
                      fontSize: 9,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      padding: '3px 8px',
                      border: '1px solid var(--border)',
                      borderRadius: 4,
                      color: 'var(--gray-3)',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <h1 style={{
              fontFamily: 'var(--heading)',
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: 800,
              letterSpacing: '-0.03em',
              color: 'var(--white)',
              lineHeight: 1.1,
              marginBottom: 20,
            }}>
              {post.title}
            </h1>
            <p style={{ fontSize: 18, color: 'var(--gray-2)', lineHeight: 1.65 }}>
              {post.subtitle}
            </p>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--border)', marginBottom: 56 }} />

          {/* Body */}
          <motion.article
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              You've been there. You spent two hours tagging a new batch of tracks — energy levels, key, color codes, comments, all of it. Hit export. Loaded the USB. Half your comments are gone. Two BPMs are wrong. A third of your tracks lost their custom ratings. And the worst part: you have no idea when it happened or how to get it back.
            </p>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-1)', marginBottom: 28 }}>
              This isn't a bug. It's a design constraint that's been baked into the rekordbox XML workflow for over a decade.
            </p>

            <h2 style={{ fontFamily: 'var(--heading)', fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--white)', marginTop: 56, marginBottom: 20 }}>
              What XML actually does to your tags
            </h2>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              rekordbox stores an extraordinary amount of metadata: track ratings, color codes, cue point names, playlist hierarchy, comment fields, analysis data like waveform position and beatgrid offsets, and the full history of your preparation activity. When you export to XML, rekordbox makes a choice about what to include.
            </p>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              It doesn't export everything. It exports a <em style={{ color: 'var(--gray-1)' }}>view</em> — a flattened, lossy serialization of a subset of your library state. Fields that don't fit the XML schema get silently dropped. Fields that do fit get mangled: Unicode characters in comments become escape sequences, custom rating scales get normalized to a different range, and timestamp fields lose millisecond precision.
            </p>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              You don't see it happen. rekordbox loads the XML on the other side and everything <em style={{ color: 'var(--gray-1)' }}>looks</em> fine — until you start digging and realize half your prep work vanished somewhere in the translation.
            </p>

            {/* Callout box */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 28px', margin: '40px 0' }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>
                The core problem
              </p>
              <p style={{ fontSize: 15, lineHeight: 1.7, color: 'var(--gray-2)' }}>
                XML is a serialization format, not a database. Every export is a snapshot — not a sync. And snapshots lose writes that happen between the export and the re-import.
              </p>
            </div>

            <h2 style={{ fontFamily: 'var(--heading)', fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--white)', marginTop: 56, marginBottom: 20 }}>
              The round-trip tax
            </h2>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              The typical DJ workflow involves at least one XML round-trip per session: export from rekordbox, open in a third-party tool, make changes, export back, re-import into rekordbox. Each round-trip compounds the loss. Comment fields get truncated. Custom cue metadata gets normalized. Energy and danceability ratings — if they're even in the XML at all — get reset.
            </p>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              For most DJs, this means either living with the loss or doing meticulous re-tagging after every import. Neither is a reasonable workflow. The software is supposed to serve the DJ, not the other way around.
            </p>

            <h2 style={{ fontFamily: 'var(--heading)', fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--white)', marginTop: 56, marginBottom: 20 }}>
              Why the .db file is the answer
            </h2>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              rekordbox stores everything — every field, every cue, every rating — in a SQLite database called{' '}
              <code style={{ background: 'var(--surface-2)', padding: '2px 8px', borderRadius: 4, fontSize: 14, color: 'var(--accent)', fontFamily: 'var(--mono)' }}>
                master.db
              </code>
              . This is the source of truth. The XML export is just a conveniently formatted excerpt.
            </p>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              Reading and writing the database directly means working at the same level of fidelity as rekordbox itself. No lossy translation. No silent drops. Every field is preserved because you're reading the same data structure the DJ software reads.
            </p>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              The complication: the database is encrypted. Pioneer DJ uses SQLCipher to protect the library data, which means a direct read requires understanding the encryption schema — and that part takes some engineering to get right.
            </p>

            <h2 style={{ fontFamily: 'var(--heading)', fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--white)', marginTop: 56, marginBottom: 20 }}>
              What Bonk! does differently
            </h2>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              Bonk! reads the master.db directly via pyrekordbox (a vendored Python bridge that handles the SQLCipher decryption) and writes changes back with the same fidelity. When you update a track's energy score or add a chapter marker, it lands in the database in the exact format rekordbox expects — no XML serialization in between.
            </p>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              The write path is atomic: changes are applied in a transaction and committed only on success. If the process is interrupted — power loss, crash, bad file — the database remains consistent. No partial writes. No corruption.
            </p>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              For DJs who want to run audio analysis on their library, Bonk! doesn't require uploading anything anywhere. All feature extraction runs locally: BPM via aubio, key detection via keyfinder-cli (same algorithm Mixxx uses), and energy/danceability via VibeNet — a locally-run ML model. Your library never leaves your machine.
            </p>

            <h2 style={{ fontFamily: 'var(--heading)', fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--white)', marginTop: 56, marginBottom: 20 }}>
              The local-first constraint
            </h2>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              Cloud-first DJ tools have an inherent tension: your library is your most valuable asset, and yet you have to trust a third party to hold it intact. Cloud services can lose data, change terms, get acquired, or simply go offline. A local-first workflow means your library is always accessible — on a plane, in a venue with poor wifi, or five years from now on whatever hardware you're using then.
            </p>

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)', marginBottom: 28 }}>
              Bonk! is built on this constraint by default. There is no cloud component. There is no account. There is no sync service. Your library, your machine, your edits.
            </p>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', marginTop: 64, marginBottom: 40 }} />

            <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--gray-2)' }}>
              The XML workflow is a workaround for a problem that didn't need to exist. As the DJ software ecosystem evolves, the tools that respect the integrity of your library — and your time — will win. Direct database access isn't a niche technical detail. It's the foundation of everything else.
            </p>
          </motion.article>
        </motion.div>
      </div>
    </div>
  );
}
